import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, BarChart3, Users, Target, Lightbulb, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const Home = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [videoError, setVideoError] = useState(false);

  const plugin = React.useRef(
    Autoplay({ delay: 5000, stopOnInteraction: true })
  );

  return (
    <div className="min-h-screen">
      {/* Hero Slider Section */}
      <section className="relative">
        <Carousel
          plugins={[plugin.current]}
          className="w-full"
          opts={{
            loop: true,
          }}
        >
          <CarouselContent>
            {/* Slide 1: Video/Image */}
            <CarouselItem>
              <div className="relative h-screen overflow-hidden">
                {!videoError ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={() => setVideoError(true)}
                  >
                    <source src="https://customer-assets.emergentagent.com/job_research-insights-2/artifacts/4pw4pbwd_0001.mp4" type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src="https://customer-assets.emergentagent.com/job_research-insights-2/artifacts/r5w33yaf_001.jpg"
                    alt="Reaksiyon Araştırma"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
                
                {/* Content */}
                <div className="absolute inset-0 flex items-center">
                  <div className="container mx-auto px-4 z-10">
                    <div className="max-w-3xl">
                      <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fadeInUp drop-shadow-2xl">
                        REAKSİYON ARAŞTIRMA
                      </h1>
                      <h2 className="text-3xl md:text-5xl font-bold text-[#F05627] mb-8 animate-fadeInUp drop-shadow-lg" style={{ animationDelay: '0.2s' }}>
                        {t('home.hero.subtitle')}
                      </h2>
                      <p className="text-xl text-white mb-12 animate-fadeInUp drop-shadow-md" style={{ animationDelay: '0.4s' }}>
                        {t('home.hero.description')}
                      </p>
                      <button
                        onClick={() => navigate('/services')}
                        className="group px-8 py-4 bg-[#F05627] text-white rounded-full font-semibold text-lg hover:bg-[#d94820] transition-all duration-300 hover:shadow-2xl hover:scale-105 animate-fadeInUp flex items-center gap-2"
                        style={{ animationDelay: '0.6s' }}
                      >
                        {t('home.hero.cta')}
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* Slide 2: Image */}
            <CarouselItem>
              <div className="relative h-screen overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_research-insights-2/artifacts/8k127puv_reaksiyon001.jpg"
                  alt="Reaksiyon Araştırma"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
                
                {/* Content */}
                <div className="absolute inset-0 flex items-center">
                  <div className="container mx-auto px-4 z-10">
                    <div className="max-w-3xl">
                      <h2 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fadeInUp drop-shadow-2xl">
                        {t('home.hero.title')}
                      </h2>
                      <h3 className="text-3xl md:text-5xl font-bold text-[#F05627] mb-8 animate-fadeInUp drop-shadow-lg" style={{ animationDelay: '0.2s' }}>
                        {t('home.hero.subtitle')}
                      </h3>
                      <p className="text-xl text-white mb-12 animate-fadeInUp drop-shadow-md" style={{ animationDelay: '0.4s' }}>
                        {t('home.hero.description')}
                      </p>
                      <button
                        onClick={() => navigate('/contact')}
                        className="group px-8 py-4 bg-[#F05627] text-white rounded-full font-semibold text-lg hover:bg-[#d94820] transition-all duration-300 hover:shadow-2xl hover:scale-105 animate-fadeInUp flex items-center gap-2"
                        style={{ animationDelay: '0.6s' }}
                      >
                        {t('home.cta.button')}
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          </CarouselContent>
          
          {/* Navigation Buttons */}
          <CarouselPrevious className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 bg-white/20 backdrop-blur-md border-white/30 hover:bg-white/30 transition-all z-20">
            <ChevronLeft className="h-6 w-6 text-white" />
          </CarouselPrevious>
          <CarouselNext className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 bg-white/20 backdrop-blur-md border-white/30 hover:bg-white/30 transition-all z-20">
            <ChevronRight className="h-6 w-6 text-white" />
          </CarouselNext>
        </Carousel>
      </section>

      {/* Qualitative Research Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fadeInLeft">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-full">
                <Users className="w-5 h-5 text-[#F05627]" />
                <span className="text-sm font-semibold text-[#F05627]">{t('home.qualitative.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                {t('home.qualitative.subtitle')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                {t('home.qualitative.description')}
              </p>
              
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.qualitative.methods')}</h3>
                <ul className="space-y-2">
                  {t('home.qualitative.methodList').map((method, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-[#F05627] rounded-full mt-2"></span>
                      <span className="text-gray-700 dark:text-gray-300">{method}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4 pt-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.qualitative.benefits')}</h3>
                <ul className="space-y-2">
                  {t('home.qualitative.benefitList').map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                      <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="relative animate-fadeInRight">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-100 to-orange-50 rounded-3xl transform rotate-3"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 transform hover:scale-105 transition-transform duration-300">
                <Users className="w-16 h-16 text-[#F05627] mb-6" />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Derinlemesine Analiz</span>
                    <span className="text-2xl font-bold text-[#F05627]">100%</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-[#F05627] rounded-full animate-progressBar"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quantitative Research Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 md:order-1 animate-fadeInLeft">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-50 rounded-3xl transform -rotate-3"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl p-8 transform hover:scale-105 transition-transform duration-300">
                <BarChart3 className="w-16 h-16 text-blue-600 mb-6" />
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    {[85, 92, 78].map((value, index) => (
                      <div key={index} className="text-center">
                        <div className="text-3xl font-bold text-blue-600">{value}%</div>
                        <div className="text-xs text-gray-600 mt-1">Metric {index + 1}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6 order-1 md:order-2 animate-fadeInRight">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-blue-600">{t('home.quantitative.title')}</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white">
                {t('home.quantitative.subtitle')}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                {t('home.quantitative.description')}
              </p>
              
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.quantitative.methods')}</h3>
                <ul className="space-y-2">
                  {t('home.quantitative.methodList').map((method, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                      <span className="text-gray-700 dark:text-white">{method}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4 pt-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{t('home.quantitative.benefits')}</h3>
                <ul className="space-y-2">
                  {t('home.quantitative.benefitList').map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2"></span>
                      <span className="text-gray-700 dark:text-white">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-900 dark:text-white mb-16 animate-fadeInUp">
            {t('home.why.title')}
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Target, text: t('home.why.reason1'), color: 'text-[#F05627]', bg: 'bg-orange-50' },
              { icon: Lightbulb, text: t('home.why.reason2'), color: 'text-blue-600', bg: 'bg-blue-50' },
              { icon: BarChart3, text: t('home.why.reason3'), color: 'text-green-600', bg: 'bg-green-50' },
              { icon: Users, text: t('home.why.reason4'), color: 'text-purple-600', bg: 'bg-purple-50' }
            ].map((item, index) => (
              <div
                key={index}
                className="group p-6 rounded-2xl bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 hover:border-[#F05627] hover:shadow-xl transition-all duration-300 animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-12 h-12 ${item.bg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className={`w-6 h-6 ${item.color}`} />
                </div>
                <p className="text-gray-700 dark:text-gray-300 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#F05627] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold animate-fadeInUp">
              {t('home.cta.title')}
            </h2>
            <p className="text-xl text-gray-300 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              {t('home.cta.description')}
            </p>
            <button
              onClick={() => navigate('/contact')}
              className="group px-8 py-4 bg-[#F05627] text-white rounded-full font-semibold text-lg hover:bg-[#d94820] transition-all duration-300 hover:shadow-xl hover:scale-105 animate-fadeInUp flex items-center gap-2 mx-auto"
              style={{ animationDelay: '0.4s' }}
            >
              {t('home.cta.button')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-lg font-semibold text-gray-300 animate-fadeInUp" style={{ animationDelay: '0.6s' }}>
              {t('home.cta.tagline')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;